<template>
	<form
		id="results-form"
		class="bg-white py-8 lg:py-10 flex flex-col justify-between"
		method="POST"
		@submit="submitForm"
	>
		<div v-if="results.length">
			<h3 class="px-6 xl:px-12 mb-6">Select a journey</h3>
			<fieldset>
				<!-- Loop through results -->
				<label
					:for="'result-' + index"
					:class="[
						{ 'border-b-gray ': index !== results.length - 1 },
						'result-item relative px-6 xl:px-12 flex items-center flex-wrap lg:flex-nowrap justify-between py-4 border-transparent border cursor-pointer gap-4 md:gap-8',
					]"
					v-for="(item, index) in topResults"
					:key="index"
				>
					<input
						type="radio"
						:id="'result-' + index"
						:value="'result-' + index"
						v-model="selected"
					/>

					<div
						class="flex-grow flex items-center flex-wrap xl:flex-nowrap justify-between"
					>
						<div class="flex gap-8 lg:gap-10 items-end pointer-events-none">
							<div>
								<span class="date">{{ item.departureTime }}</span>
								<p class="heading-h5 mb-0">{{ item.fromPort }}</p>
							</div>
							<span class="w-5 h-5 mb-1 fill-black">
								<svg
									class="w-full h-full fill-inherit"
									xmlns="http://www.w3.org/2000/svg"
									viewBox="0 0 200 200"
								>
									<polygon
										fill-rule="evenodd"
										clip-rule="evenodd"
										points="121.3,21.3 106.3,36.4 159.2,89.3 0,89.3 0,110.7 159.2,110.7
                                        106.3,163.6 121.3,178.7 192.4,107.5 200,100 192.4,92.4 "
									/>
								</svg>
							</span>
							<div>
								<span class="date">{{ item.arrivalTime }}</span>
								<p class="heading-h5 mb-0">{{ item.toPort }}</p>
							</div>
						</div>
						<div
							class="w-full md:w-auto md:min-w-[80px] lg:w-full xl:w-[80px] flex md:block items-center gap-4 mt-3 md:mt-0 lg:mt-3 xl:mt-0 pointer-events-none"
						>
							<span class="route">Duration</span>
							<p class="heading-h5 mb-0">{{ item.duration }}</p>
						</div>
					</div>
				</label>
				<!-- End of For Loop -->
			</fieldset>
			<div class="px-6 xl:px-12 pt-8" v-if="currentPage !== totalPages">
				<button
					class="button primary-dark w-full justify-between"
					@click="nextPage"
					:disabled="currentPage == totalPages"
				>
					View More
					<span class="w-3 h-3">
						<svg
							class="w-full h-full fill-inherit"
							xmlns="http://www.w3.org/2000/svg"
							viewBox="0 0 200 200"
						>
							<rect x="84.6" y="0" width="30.8" height="200" />
							<rect
								y="84.6"
								fill-rule="evenodd"
								clip-rule="evenodd"
								width="200"
								height="30.8"
							/>
						</svg>
					</span>
				</button>
			</div>
		</div>
		<div v-else>
			<h3 class="px-6 xl:px-12 mb-6">No Results</h3>
			<p class="px-6 xl:px-12 content-block">
				Unfortunately nothing matches your selected options. You can try a
				different date, or
				<a href="/contact" title="Contact us">contact us</a> to find out how we
				can help.
			</p>
		</div>
	</form>
</template>

<script>
import axios from "axios";
import { polyfill } from "smoothscroll-polyfill";

/**
        title: Schedule Form
		props: {
			- results: These are the results returned from the predator/schedule/all API and passed down from the parent component.
		},
        data: {
			- selected: This is the result which has been selected.
			- currentPage: This is the current results page. This will be used to display a 'View More' button if there are more than 5 results.
		}
        filters: {
            - pluralise: Make sure the Results text is correct based on number of results returned
        },
		computed: {
			- topResults: This will display the top 5 results initially when the instance has been created.
			- totalPages: This will find how many pages of results are returned.
		},
		watch: {
			- selected: Watch for when a journey has been selected from the given results, and then run the activeJourney method.
		},
        methods: {
			- nextPage: Used so we can display the View More button if there are more than 5 results.
			- activeJourney: Get the active journey by ID, and pass this through to the submitForm method.
			- submitForm: Add the selected journey to the parent component so it can be used by the Journey component.
		}
    **/
export default {
	props: ["results"],
	data: function () {
		return {
			selected: null,
			currentPage: 1,
		};
	},
	filters: {
		pluralise: function (count, single, plural) {
			// Decide to pluralise based on the count
			if (count == 1) {
				return count + ` ${single}`;
			} else if (count > 1) {
				return count + ` ${plural}`;
			} else {
				return "Please pass valid count of data";
			}
		},
	},
	computed: {
		topResults: function () {
			return this.results.slice(0, this.currentPage * 5);
		},
		totalPages: function () {
			return Math.ceil(this.results.length / 5);
		},
	},
	watch: {
		results: {
			immediate: true,
			handler: function () {
				this.selected = null;
			},
		},
		selected: {
			handler: function (after, before) {
				if (after !== before) {
					this.activeJourney(this.selected);

					// Scroll User to the journey once it displays
					let formContainer = document.querySelector("#journey-details");

					if (formContainer) {
						formContainer =
							document.querySelector("#journey-details").offsetTop - 50;

						// Add the polyfill so Scroll will work on Safari
						polyfill();

						scroll({
							top: formContainer,
							behavior: "smooth",
						});
					}
				}
			},
		},
	},
	methods: {
		nextPage: function (event) {
			event.preventDefault();
			if (this.currentPage < this.totalPages) {
				this.currentPage++;
			}
		},
		activeJourney: function (id) {
			// Get the Results with that ID
			const active = document.querySelector('label[for="' + id + '"]');

			// Add a Class to Selected Route
			const results = document.querySelectorAll(".result-item");
			results.forEach(function (result) {
				result.classList.remove("result-item--active");
			});
			if (active) {
				active.classList.add("result-item--active");
			}

			// Submit the data
			this.submitForm(id.replace("result-", ""));
		},
		submitForm: function (index) {
			const selectedItem = this.results[index];
			this.$emit("findResults", selectedItem);
		},
	},
};
</script>
